// IndentList — read-only REPORT of all indent / purchase-order lines.
// Receiving stock now happens through the normal Store Purchase flow (the
// purchase screen shows a supplier's pending indents when that supplier is
// picked), so there are no Received / Cancelled actions here anymore.
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import config from '../../config';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const Companycurrency = company?.currency ?? '';
const token = sessionStorage.getItem('token');

const STATUS_COLOR = {
  Pending: 'warning',
  Received: 'success',
  Cancelled: 'error',
};

export default function IndentList() {
  const [list, setList] = useState([]);
  const [status, setStatus] = useState('Pending');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });

  const showSnack = (msg, sev = 'success') =>
    setSnack({ open: true, msg, sev });

  const load = useCallback(async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/indent/list/${company.id}?status=${status}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = res.ok ? await res.json() : [];
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Indent list error:', err);
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = list.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [r.orderNo, r.productName, r.supplierName, r.remark, r.status]
      .filter(Boolean)
      .some((v) => v.toString().toLowerCase().includes(q));
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        flexWrap='wrap'
        gap={1}
        mb={2}>
        <Typography variant='h5' fontWeight={800} sx={{ color: '#0a2540' }}>
          📋 Indent / Purchase Orders
        </Typography>
        <Box display='flex' gap={1} alignItems='center' flexWrap='wrap'>
          <FormControl size='small' sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label='Status'
              value={status}
              onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value='Pending'>Pending</MenuItem>
              <MenuItem value='Received'>Received</MenuItem>
              <MenuItem value='Cancelled'>Cancelled</MenuItem>
              <MenuItem value='all'>All</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size='small'
            placeholder='🔍 Search'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button
            variant='outlined'
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box display='flex' justifyContent='center' py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2 }}>
          <TableContainer sx={{ maxHeight: '74vh' }}>
            <Table stickyHeader size='small'>
              <TableHead>
                <TableRow>
                  {[
                    'Order No',
                    'Date',
                    'Product',
                    'Qty',
                    'Supplier',
                    `Price (${Companycurrency})`,
                    'Remark',
                    'Status',
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        backgroundColor: '#0a2540',
                        color: '#fff',
                        fontWeight: 'bold',
                      }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align='center'>
                      No indents found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((r) => (
                    <TableRow key={r.detailId} hover>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>
                        {r.orderNo}
                      </TableCell>
                      <TableCell>
                        {r.orderDate
                          ? new Date(r.orderDate).toLocaleDateString('en-GB')
                          : '-'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {r.productName}
                      </TableCell>
                      <TableCell>{Number(r.qty)}</TableCell>
                      <TableCell>{r.supplierName || '-'}</TableCell>
                      <TableCell>
                        {Companycurrency} {Number(r.price || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>{r.remark || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={r.status}
                          size='small'
                          color={STATUS_COLOR[r.status] || 'default'}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.sev} variant='filled'>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
